/**
 * RepertoirePlayContainerOSMD
 *
 * React component that hosts the PhaserGame for OSMD-based repertoire rendering.
 * Parallels RepertoirePlayContainer (SVG version) but replaces the SVG Staff component
 * with a Phaser scene (OSMDRepertoireScene) that renders MusicXML directly.
 *
 * Responsibilities:
 * - Mount PhaserGame and start OSMDRepertoireScene
 * - Send phrase data to the scene via AddAllOSMDPhrases event
 * - Forward MIDI events to Phaser for NoteContainer visual feedback
 * - Forward play/pause state changes to Phaser
 * - Listen for RepertoireCompleted / RepertoirePhraseCompleted from Phaser
 * - Dispatch backend tracking events (play, pause, playing, completed, phrase completed)
 * - Forward soft-refresh to Phaser when updateKey changes
 */

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Phaser from 'phaser';
import { useDispatch, useSelector } from 'react-redux';
import { useWakeLock } from 'Hooks/useWakeLock';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { find } from 'lodash';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { useExpectedMidiHints } from 'AudioRecognition';

import { PhaserGame } from 'Phaser/PhaserGame';
import type { IRefPhaserGame } from 'Phaser/PhaserGame';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import { TimingOffsetsConfig } from 'Models/EventStream';
import Phrase from 'Models/Phrase';
import { useRepertoirePlayContext } from 'Contexts/RepertoirePlayContext';
import { useMidiContext } from 'Contexts/MidiContext';
import {
  PhaserToReactListeners,
  NotifyPhaser,
  NotifyPhaserEvents,
  RemoveListener,
  type StartPlayingArgs,
} from 'Phaser/EventBus2';
import { AddAllOSMDPhrasesHelper } from 'Phaser/EventBus2/ToPhaser';
import {
  RepertoireReducer,
  REPERTOIRE_PROGRESS,
  PERFECT_PHRASE_DEFAULT_COUNT,
} from 'Types/RepertoireTypes';
import { MainAppReducer, ErrorRecognitionActive, MidiEventState } from 'Types';
import * as eventActions from 'Actions/events';
import { CustomCircularProgress } from 'Components/StyledComponents';
import FeedbackToast from 'Components/FeedbackToast';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import { IntervalTimer } from 'Utils/IntervalTimer';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import { debounce } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getNamesConcatted } from 'Utils';
import { getDeviceClass } from 'Utils/Responsive';
import * as repertoireActions from 'Actions/repertoire';
import type { AppDispatch } from 'Reducers';
import {
  applyParallelModeConversionToMusicXml,
  type ParallelModeConversion,
} from 'Utils/MusicXmlModeConversion';
import useRepertoireNotePlayback from './components/useRepertoireNotePlayback';
import {
  repertoirePlaybackModeDisablesRecognition,
  repertoirePlaybackModePlaysNotes,
} from './shared/repertoirePlayback';
import { getScoreThemePalette, normalizeScoreTheme } from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import { getRepertoireProgressNoteCount } from 'Phaser/Scenes/RepertoireLazyLoadPolicy';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RepertoirePlayContainerOSMDProps {
  showKeyboard: boolean;
  setAccuracy: (accuracy: number) => void;
  setShowAccuracy: (show: boolean) => void;
  cursorIsVisible: boolean;
  transposeSemitones: number;
  modeConversion: ParallelModeConversion;
  headerVisible: boolean;
  scrollPosition?: number | null;
  setScrollPosition?: React.Dispatch<React.SetStateAction<number | null>>;
  scoreTheme?: ScoreTheme;
}

// How often (ms) to dispatch the repertoirePlayingEventAction
const PLAY_TIME_SEND_INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RepertoirePlayContainerOSMD: React.FC<RepertoirePlayContainerOSMDProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  useWakeLock();

  // ---- Analytics ----
  useEffect(() => {
    awsRum?.recordPageView(location.pathname);
    ReactGA?.send({ hitType: 'pageview', page: location.pathname });
  }, [location.pathname]);

  // ---- Scene cleanup on container unmount ----
  useEffect(() => {
    return () => {
      console.log('[LOOP-DBG] Container unmount — sending UnloadExercise');
      NotifyPhaserEvents.UnloadExercise();
    };
  }, []);

  // ---- Context ----
  const {
    midiStream,
    phraseParts,
    allPhrasesLoaded,
    musicLoaded,
    setMusicLoaded,
    performanceTempo,
    updateKey,
    setDeferSchedulerStart,
    setForwardMidiToPhaser,
    setSkipContextEventDispatching,
    localPlaySessionId,
    scheduler2,
    isLoopingActive,
    repertoirePlaybackMode,
  } = useRepertoirePlayContext();

  // ---- Redux ----
  const appData = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const scoreTheme = normalizeScoreTheme(
    props.scoreTheme ?? appData.userData?.practice_score_theme,
    appData.userData?.practice_dark_mode,
  );
  const scorePalette = getScoreThemePalette(scoreTheme);
  const errorRecognitionActive: ErrorRecognitionActive = appData.errorRecognitionActive;
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const repertoireIdSelect = repertoireReducerData.repertoireIdSelect;
  const repertoirePlaying = repertoireReducerData.repertoirePlaying;
  const userRepertoireProgress = repertoireReducerData.userRepertoireProgresses;
  const repertoireData = repertoireReducerData.repertoireData;
  const cursorVisible = repertoireReducerData.cursorVisible;

  // Derive tempo the same way RepertoirePlayContext does
  const repertoireSelected = useMemo(
    () => find(repertoireData, (rd) => rd.id === repertoireIdSelect),
    [repertoireData, repertoireIdSelect],
  );
  const repertoireProgressSelected = useMemo(
    () => find(userRepertoireProgress, (urd) => urd?.repertoire?.id === repertoireIdSelect),
    [userRepertoireProgress, repertoireIdSelect],
  );
  const currentTempo = repertoireProgressSelected?.last_tempo || performanceTempo || 72;
  const playbackModePlaysNotes = repertoirePlaybackModePlaysNotes(repertoirePlaybackMode);
  const listenOnlyPlayback = repertoirePlaybackModeDisablesRecognition(repertoirePlaybackMode);

  // Use the context's localPlaySessionId — the context manages the play
  // session lifecycle and actively clears Redux's playSessionId to null,
  // making the Redux value unreliable for the OSMD container.
  const playSessionId = localPlaySessionId;

  // MIDI forwarding is now handled by the RepertoirePlayContext via
  // forwardMidiToPhaser flag — no separate MidiContext listener needed.

  // ---- Refs ----
  const phaserRef = useRef<IRefPhaserGame | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const phrasesSentRef = useRef(false);
  /** Unique render request id. It prevents a late scene-ready event from an
   * old piece (or an earlier mount of the same piece) from dismissing the
   * current score's loading state. */
  const scoreLoadIdRef = useRef(uuid());
  /** Latest repertoireSelected from Redux, stored in a ref so loadScene can
   *  read it without adding it to useCallback deps (which would destabilize
   *  the callback and risk missing the one-time PhaserReady event). */
  const repertoireSelectedRef = useRef(repertoireSelected);
  repertoireSelectedRef.current = repertoireSelected;
  /** Always-current zoom level from Redux, readable from loadScene without
   *  adding it to useCallback deps. */
  const zoomLevelRef = useRef(appData.zoomLevel ?? 0.9);
  zoomLevelRef.current = appData.zoomLevel ?? 0.9;
  /** Always-current repertoireIdSelect, readable from interval/timeout callbacks
   *  whose closures captured a stale value at effect registration time. */
  const repertoireIdSelectRef = useRef(repertoireIdSelect);
  repertoireIdSelectRef.current = repertoireIdSelect;
  /** Always-current playSessionId, readable from interval/timeout callbacks. */
  const playSessionIdRef = useRef(playSessionId);
  playSessionIdRef.current = playSessionId;
  /** Always-current cursorVisible, readable from callbacks that might
   *  capture a stale closure value (e.g. loadScene). */
  const cursorVisibleRef = useRef(cursorVisible);
  cursorVisibleRef.current = cursorVisible;
  /** The user's explicit cursor preference, updated only by user toggles.
   *  Lifecycle dispatches from the context (startScheduler → true,
   *  softRefreshRepertoire → false) are detected and filtered out so
   *  this ref always reflects the user's last deliberate choice. */
  const userCursorPrefRef = useRef(cursorVisible);
  /** When true, the next cursorVisible Redux change is a lifecycle dispatch
   *  (e.g. from startScheduler) and should NOT update userCursorPrefRef. */
  const cursorSyncSuppressedRef = useRef(false);
  const sceneReadyRef = useRef(false);
  /** True after phrases have been sent at least once. Used to distinguish
   *  first load (PhaserReady already called loadScene) from subsequent
   *  piece changes (need to manually restart the scene). */
  const hasEverSentPhrasesRef = useRef(false);
  /** Prevent duplicate browser restore events from starting overlapping scene
   * rebuilds. Cleared only when the replacement score reports ready. */
  const renderRecoveryInProgressRef = useRef(false);

  useEffect(() => {
    logger.info('[CURSOR-VIS] Container mount/initial', {
      cursorVisible,
      userCursorPrefRef: userCursorPrefRef.current,
      cursorVisibleRef: cursorVisibleRef.current,
    });
  }, []);

  const prevUpdateKeyRef = useRef(updateKey);
  const sendPlayTimeIntervalRef = useRef<IntervalTimer | undefined>(undefined);
  const accumulatedTimeRef = useRef(0);
  const lastMeasuredTimeRef = useRef(0);
  const isFirstPlayPauseRef = useRef(true);
  /** Whether the first MIDI note has been played (count-in completed).
   *  Used to distinguish first-play (deferred scheduler) from resume
   *  (scheduler must be recalibrated before the scene starts updating). */
  const hasPlayedOnceRef = useRef(false);
  /** The global measure the cursor was repositioned to on pause.
   *  Used on resume to pass as measureOffset to startScheduler() so the
   *  TimeKeeper starts from exactly where the cursor was placed. */
  const pausedAtMeasureRef = useRef<number | undefined>(undefined);
  /** The actual OSMD global timestamp where the cursor was positioned on pause.
   *  Used to compute the correct TimeKeeper startMeasure on resume, since
   *  the raw measure index * measureDurationAt(0) may not match the actual
   *  OSMD timestamp (especially in non-4/4 time signatures). */
  const pausedAtTimestampRef = useRef<number | undefined>(undefined);
  const eventStreamUpdateLoopStartedRef = useRef(false);
  const accuracyLoopStartedRef = useRef(false);

  // Latest setAccuracy, mirrored into a ref so the phase listener can reset the
  // displayed accuracy without re-subscribing on every render.
  const setAccuracyRef = useRef(props.setAccuracy);
  setAccuracyRef.current = props.setAccuracy;

  // ---- Note timing feedback toast ----
  // OSMDEventStream fires ShowFeedbackToast on early/late/wrong onset (and
  // early/late offset). Mirror the sight-reading experimental toast here so the
  // repertoire flow surfaces the same timing feedback. Gated on the
  // USE_ERROR_FEEDBACK experimental flag.
  const [feedbackToastText, setFeedbackToastText] = useState<string | null>(null);
  const feedbackToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showFeedbackToast = useCallback(({ state }: { state: MidiEventState }) => {
    if (!isFeatureEnabled(FeatureFlag.USE_ERROR_FEEDBACK)) return;
    // Distinct label per state so the toast pinpoints WHY a note is yellow —
    // onset timing vs an extra/duplicate match vs a release-timing miss.
    let text = '';
    switch (state) {
      case MidiEventState.LATE_ONSET:
        text = 'Onset: too late';
        break;
      case MidiEventState.EARLY_ONSET:
        text = 'Onset: too early';
        break;
      case MidiEventState.WRONG_NOTE:
        text = 'Wrong note!';
        break;
      case MidiEventState.ADDITIONAL_NOTE:
        text = 'Extra note';
        break;
      case MidiEventState.NEVER_PLAYED:
        text = 'Missed';
        break;
      case MidiEventState.LATE_OFFSET:
        text = 'Release: too long';
        break;
      case MidiEventState.EARLY_OFFSET:
        text = 'Release: too short';
        break;
      default:
        return;
    }
    setFeedbackToastText(text);
    if (feedbackToastTimerRef.current) {
      clearTimeout(feedbackToastTimerRef.current);
    }
    feedbackToastTimerRef.current = setTimeout(() => {
      setFeedbackToastText(null);
      feedbackToastTimerRef.current = null;
    }, 1000);
  }, []);

  // Always register the listener (even when the flag is off) so the events that
  // OSMDEventStream emits are consumed rather than piling up in the EventBus
  // queue; the flag gate lives inside showFeedbackToast.
  useEffect(() => {
    PhaserToReactListeners.ShowFeedbackToast(showFeedbackToast);
    return () => {
      RemoveListener('ShowFeedbackToast', showFeedbackToast);
      if (feedbackToastTimerRef.current) {
        clearTimeout(feedbackToastTimerRef.current);
        feedbackToastTimerRef.current = null;
      }
    };
  }, [showFeedbackToast]);

  // ---- Perfect Phrase Counter ----
  // Forward the PPC config (enabled + qualifying streak) from Redux to the
  // Phaser scene, which drives the streak-checkbox badge and "perfected"
  // detection. Re-emits whenever the user toggles the counter or adjusts the
  // count in the looping menu.
  // Fallbacks cover users whose persisted state predates these fields (the
  // repertoire slice rehydrates via redux-persist `hardSet`).
  const perfectPhraseEnabled = repertoireReducerData.perfectPhraseEnabled ?? false;
  const perfectPhraseCount =
    repertoireReducerData.perfectPhraseCount ?? PERFECT_PHRASE_DEFAULT_COUNT;
  useEffect(() => {
    NotifyPhaserEvents.SetPerfectPhraseConfig({
      enabled: perfectPhraseEnabled,
      targetStreak: perfectPhraseCount,
    });
  }, [perfectPhraseEnabled, perfectPhraseCount]);

  // "You have perfected this section" toast, fired by the scene when the user
  // completes the qualifying streak. Shown at the bottom of the play area.
  const [perfectedToastVisible, setPerfectedToastVisible] = useState(false);
  const perfectedToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const onPerfected = () => {
      setPerfectedToastVisible(true);
      if (perfectedToastTimerRef.current) {
        clearTimeout(perfectedToastTimerRef.current);
      }
      perfectedToastTimerRef.current = setTimeout(() => {
        setPerfectedToastVisible(false);
        perfectedToastTimerRef.current = null;
      }, 3000);
    };
    PhaserToReactListeners.SectionPerfected(onPerfected);
    return () => {
      RemoveListener('SectionPerfected', onPerfected);
      if (perfectedToastTimerRef.current) {
        clearTimeout(perfectedToastTimerRef.current);
        perfectedToastTimerRef.current = null;
      }
    };
  }, []);

  // ---- Looping state (from context) ----
  // Forward looping active state to the Phaser scene for bracket rendering.
  // The scene's setLoopingActive (driven by this event) dispatches
  // USER_TOGGLE_LOOP into the playback machine.
  useEffect(() => {
    console.log('[LOOPING DEBUG] React useEffect firing, isLoopingActive:', isLoopingActive);
    logger.info('[LOOPING] isLoopingActive changed', { isLoopingActive });
    NotifyPhaserEvents.SetLoopingActive({ active: isLoopingActive });
  }, [isLoopingActive]);

  // ---- MUS-243: Inject Scheduler2 into the Phaser scene ----
  // The scene's playback machine actions need to drive Scheduler2 directly
  // (pause / unpause / setLoopBounds). The scene listens for InjectScheduler2,
  // stores the reference, and instantiates its XState actor on receipt.
  // We emit on every change of scheduler2 — including the initial creation —
  // so the scene gets the live instance. Re-emission after scene reloads is
  // handled by re-running this effect when sceneReadyRef stabilizes (the
  // scene's setScheduler2 short-circuits if the actor is already running).
  useEffect(() => {
    if (!scheduler2) return;
    console.log('[LOOP-DBG-243] Container: emitting InjectScheduler2');
    NotifyPhaserEvents.InjectScheduler2({ scheduler2 });
  }, [scheduler2]);

  // ---- MUS-243: Mirror PlaybackPhase from the machine into Redux ----
  // The reducer's SET_PLAYBACK_PHASE case writes `playbackPhase`,
  // `isLoopingActiveFromMachine`, and `hasEverPlayedInThisLoop` from the
  // machine's broadcast. The legacy `repertoirePlaying` Redux field is
  // deliberately NOT mirrored here — it still represents user intent
  // (set by the play/pause button) and feeds the play/pause useEffect
  // that bridges Redux → EventBus → machine. Auto-mirroring would create
  // a feedback loop (machine broadcasts phase → repertoirePlaying flips
  // → useEffect re-fires → emits opposite intent).
  useEffect(() => {
    const handlePlaybackPhaseChanged = (data: {
      phase: import('Phaser/PlaybackMachine/playbackMachine.types').PlaybackPhase;
      isLoopingActive: boolean;
      hasEverPlayedInThisLoop: boolean;
    }) => {
      console.log(
        `[LOOP-DBG-243] Container: PlaybackPhaseChanged phase=${data.phase} isLoop=${data.isLoopingActive} hasEverPlayed=${data.hasEverPlayedInThisLoop}`,
      );
      dispatch(repertoireActions.setPlaybackPhase(data));
    };
    PhaserToReactListeners.PlaybackPhaseChanged(handlePlaybackPhaseChanged);
    return () => {
      RemoveListener('PlaybackPhaseChanged', handlePlaybackPhaseChanged);
    };
  }, [dispatch]);

  // ---- Listen for loop bounds from Phaser ----
  // MUS-243: Scheduler2 bound management has moved into the playback
  // machine (`setScheduler2LoopBounds` and `clearScheduler2` facade
  // actions). This listener now only handles Redux state side effects
  // (clearing `repertoireEnded`); the machine's PlaybackPhaseChanged
  // emissions drive `repertoirePlaying` via the auto-mirror.
  useEffect(() => {
    const handleSetLoopBounds = (data: { startTimestamp: number; endTimestamp: number } | null) => {
      if (data) {
        console.log(
          `[LOOP-DBG] Container: SetLoopBounds received (machine drives scheduler2) start=${data.startTimestamp} end=${data.endTimestamp}`,
        );
        dispatch(repertoireActions.setRepertoireEnded(false));
      } else {
        console.log(`[LOOP-DBG] Container: SetLoopBounds null (machine drives clearScheduler2)`);
      }
    };
    PhaserToReactListeners.SetLoopBounds(handleSetLoopBounds);
    return () => {
      RemoveListener('SetLoopBounds', handleSetLoopBounds);
    };
  }, [dispatch]);

  // ---- Defer scheduler start for OSMD ----
  // Tell the shared RepertoirePlayContext to skip scheduler2.unpauseTicks()
  // in onStart(). The scheduler will instead be started by startScheduler()
  // when the Phaser scene emits StartedPlaying (on the first MIDI note in
  // practice modes, or automatically after the count-in in listen mode).
  useEffect(() => {
    console.log(`[LOOP-DBG] Container: setting deferSchedulerStart = true`);
    setDeferSchedulerStart(true);
    return () => {
      console.log(`[LOOP-DBG] Container: setting deferSchedulerStart = false (unmount)`);
      setDeferSchedulerStart(false);
    };
  }, [setDeferSchedulerStart]);

  // ---- Skip context event dispatching in OSMD mode ----
  // The context's sendPlayTimeInterval and onCalcTheEnd dispatch
  // RepertoirePlayingEvent and RepertoireCompletedEvent using midiStream,
  // which has zero events in OSMD mode (events are in osmdEventStream).
  // This container handles its own event dispatching using osmdEventStream,
  // so tell the context to skip its duplicate dispatches.
  useEffect(() => {
    setSkipContextEventDispatching(true);
    return () => {
      setSkipContextEventDispatching(false);
    };
  }, [setSkipContextEventDispatching]);

  // ---- Forward MIDI to Phaser ----
  // Tell the shared RepertoirePlayContext to forward MIDI events to the
  // Phaser EventBus via NotifyPhaser(MidiOn/MidiOff). This lives in the
  // context's own MIDI listener (which survives clearListeners()) rather
  // than a separate listener that gets wiped by clearListeners().
  useEffect(() => {
    console.log(`[LOOP-DBG] Container: setting forwardMidiToPhaser = true`);
    logger.debug('[OSMD-MIDI] Container: setting forwardMidiToPhaser = true');
    setForwardMidiToPhaser(true);
    return () => {
      console.log(`[LOOP-DBG] Container: setting forwardMidiToPhaser = false (unmount)`);
      logger.debug('[OSMD-MIDI] Container: setting forwardMidiToPhaser = false (unmount)');
      setForwardMidiToPhaser(false);
    };
  }, [setForwardMidiToPhaser]);

  // ---- Dimensions ----
  // Stable game dimensions passed to PhaserGame — set once and never updated.
  // PhaserGame's useLayoutEffect depends on [ref, height, width]; if these
  // change, the entire Phaser game is destroyed and recreated, wiping all
  // phrases, cursors, and event listeners. All subsequent resizing is handled
  // by the scene's resize() method via the ResizeWindow event bus event.
  const [gameWidth] = useState(() => Math.max(window.innerWidth, 960));
  // The header and footer are position:fixed overlays, so the canvas can fill
  // the full viewport height. Content behind the header/footer is handled by
  // the scene's top padding (for the toolbar) and is scrollable.
  const [gameHeight] = useState(() => Math.max(400, window.innerHeight));

  // Responsive wrapper height for the container div CSS — tracks available
  // vertical space so the Phaser canvas wrapper grows/shrinks with the window.
  // This state does NOT flow into PhaserGame props, so updating it won't
  // trigger game destruction.
  const [wrapperHeight, setWrapperHeight] = useState(() => Math.max(400, window.innerHeight));

  // ---- OSMDEventStream for Phaser scene ----
  // Uses the SAME timeKeeper as the context's midiStream / scheduler2.
  // midiStream.timeKeeper is the context's single TimeKeeper instance,
  // always defined at mount time (unlike scheduler2 which starts null).
  const [osmdEventStream] = useState(() => {
    const stream = new OSMDEventStream(
      midiStream.timeKeeper,
      new TimingOffsetsConfig(),
      appData.userData?.colorblind_type || null,
      1,
      0.025,
      0.1,
      true,
    );
    logger.debug('[OSMD-MIDI] Container: created osmdEventStream', {
      osmdEventStreamId: stream.id,
      contextMidiStreamId: midiStream.id,
      sharedTimeKeeper: '(same object ref from midiStream.timeKeeper)',
    });
    return stream;
  });
  const { preferredInputMode, audioInputLatencyMs } = useMidiContext();

  useEffect(() => {
    osmdEventStream.setInputLatencyMs(audioInputLatencyMs);
  }, [audioInputLatencyMs, osmdEventStream]);

  // ---- Brute-force loop accuracy reset ----
  // Loop accuracy is (for now) just the shared piece accuracy, reset to 100% at
  // the start of every loop iteration. The scene already re-inits the accuracy
  // queue on the loopCountInWait entry; we also snap the displayed value here
  // so it doesn't lag a poll tick behind. `loopCountInWait` is reached before
  // the first note of each iteration (and on the very first loop start), so
  // this never wipes a just-played note.
  // TODO: track loop accuracy separately from whole-piece accuracy.
  const playbackPhase = repertoireReducerData.playbackPhase;
  useEffect(() => {
    if (isLoopingActive && playbackPhase === 'loopCountInWait') {
      osmdEventStream.initAccuracyQueues();
      setAccuracyRef.current(100);
    }
  }, [playbackPhase, isLoopingActive, osmdEventStream]);

  useExpectedMidiHints({
    enabled: preferredInputMode === 'audio' && !listenOnlyPlayback,
    getExpectedMidiNotes: () => osmdEventStream.getExpectedMidiNotes(),
  });

  useRepertoireNotePlayback({
    enabled: playbackModePlaysNotes,
    isPlaying: repertoirePlaying,
    scheduler: scheduler2,
    eventStream: osmdEventStream,
    intervalName: 'repertoire-osmd-note-playback',
  });

  const headerVisible = props.headerVisible;

  // ---- Phrases ----
  const phrases = useMemo((): Phrase[] => {
    if (allPhrasesLoaded) {
      const partsArray: Array<Phrase | undefined> = phraseParts.toArray();
      return partsArray.filter((phrase): phrase is Phrase => phrase !== undefined);
    }
    return [];
  }, [phraseParts, allPhrasesLoaded]);
  /** Stable full-piece denominator while the scene only has a lazy subset of
   * phrase events instantiated. This prevents progress from jumping forward
   * and then regressing as later render batches join the event stream. */
  const totalScoreNoteCount = useMemo(
    () => phrases.reduce((total, phrase) => total + phrase.getNumNotes(), 0),
    [phrases],
  );
  const totalScoreNoteCountRef = useRef(totalScoreNoteCount);
  totalScoreNoteCountRef.current = totalScoreNoteCount;

  const transformedPhrasePayloads = useMemo(() => {
    return phrases.map((phrase) => ({
      phrase,
      musicXml: applyParallelModeConversionToMusicXml(phrase.musicXML.xml, props.modeConversion),
    }));
  }, [phrases, props.modeConversion]);

  // Phrase downloads completing is only the first half of loading. Keep the
  // spinner visible while Phaser transactionally renders and lays out OSMD.
  useEffect(() => {
    if (allPhrasesLoaded) {
      setMusicLoaded(false);
      // Diagnostic: log both event stream states when phrases are loaded
      logger.info('[ACC-DBG] allPhrasesLoaded — event stream state', {
        osmdEventStreamId: osmdEventStream.id,
        osmdAccuracyQueueLength: osmdEventStream.accuracyQueueLength(),
        osmdTotalNotes: osmdEventStream.totalNotesLength(),
        contextMidiStreamId: midiStream.id,
        contextMidiAccuracyQueueLength: midiStream.accuracyQueueLength(),
        contextMidiTotalNotes: midiStream.totalNotesLength(),
      });
    }
  }, [allPhrasesLoaded, setMusicLoaded, osmdEventStream, midiStream]);

  useEffect(() => {
    const handleScoreReady = ({
      loadId,
      phraseCount,
      renderedPhraseCount,
    }: {
      loadId?: string;
      phraseCount: number;
      renderedPhraseCount?: number;
    }) => {
      if (loadId !== scoreLoadIdRef.current) {
        logger.warn('[OSMDRepertoire] Ignoring stale score-ready event', {
          receivedLoadId: loadId,
          expectedLoadId: scoreLoadIdRef.current,
          phraseCount,
        });
        return;
      }
      logger.info('[OSMDRepertoire] Score render ready', {
        loadId,
        phraseCount,
        renderedPhraseCount,
      });
      renderRecoveryInProgressRef.current = false;
      setMusicLoaded(true);
    };

    PhaserToReactListeners.RepertoireScoreReady(handleScoreReady, undefined, false, true);
    return () => {
      RemoveListener('RepertoireScoreReady', handleScoreReady);
    };
  }, [setMusicLoaded]);

  // ---- Reset container state when the selected piece changes ----
  // The container no longer remounts on piece change (key prop no longer
  // includes repertoireIdSelect), so we reset per-piece state here.
  const prevRepIdRef = useRef(repertoireIdSelect);
  useEffect(() => {
    if (prevRepIdRef.current !== repertoireIdSelect && repertoireIdSelect) {
      prevRepIdRef.current = repertoireIdSelect;

      NotifyPhaserEvents.UnloadExercise();

      scoreLoadIdRef.current = uuid();
      setMusicLoaded(false);
      phrasesSentRef.current = false;
      isFirstPlayPauseRef.current = true;
      hasPlayedOnceRef.current = false;
      eventStreamUpdateLoopStartedRef.current = false;
      accuracyLoopStartedRef.current = false;

      osmdEventStream.reset();
    }
  }, [repertoireIdSelect, osmdEventStream]);

  // BPM sync is handled by the context (RepertoirePlayContext line 865:
  // timeKeeper.setBpm(tempo)). Since osmdEventStream shares the same
  // timeKeeper via midiStream.timeKeeper, no additional sync is needed here.

  // ---- Sync error recognition (hand separation) to osmdEventStream ----
  // The context syncs errorRecognitionActive to midiStream (line 324), but in
  // OSMD mode midiStream has no events — osmdEventStream is the one that
  // processes MIDI input, colors notes, and calculates accuracy. Without this
  // sync, the hand toggle in the UI has no effect on note matching or coloring.
  useEffect(() => {
    const effectiveErrorRecognitionActive = listenOnlyPlayback
      ? { left: false, right: false }
      : errorRecognitionActive;
    osmdEventStream.setErrorRecognitionActive(effectiveErrorRecognitionActive);
    logger.debug('[OSMD-MIDI] Container: synced errorRecognitionActive to osmdEventStream', {
      left: effectiveErrorRecognitionActive.left,
      right: effectiveErrorRecognitionActive.right,
      listenOnlyPlayback,
    });
  }, [errorRecognitionActive, listenOnlyPlayback, osmdEventStream]);

  // ---- Sync cursor visibility to Phaser scene ----
  // The settings toggle updates rep.cursorVisible in Redux. Forward this to
  // the Phaser scene so it can show/hide cursor graphics without affecting
  // the cursor's internal position tracking.
  //
  // The shared context also dispatches setCursorVisible during lifecycle
  // transitions (softRefreshRepertoire dispatches false, startScheduler
  // dispatches true). These are NOT user preference changes:
  //  - softRefreshRepertoire changes are detected by updateKey also changing
  //    in the same render cycle.
  //  - startScheduler changes are detected by cursorSyncSuppressedRef.
  useEffect(() => {
    const isSoftRefresh = updateKey !== prevUpdateKeyRef.current;
    const isSuppressed = cursorSyncSuppressedRef.current;
    // Only update the user preference ref for genuine user toggles.
    if (!isSoftRefresh && !isSuppressed) {
      userCursorPrefRef.current = cursorVisible;
    }
    if (!sceneReadyRef.current) return;
    logger.info('[CURSOR-VIS] Container useEffect: sending SetCursorVisible', {
      cursorVisible,
      isSoftRefresh,
      isSuppressed,
    });
    NotifyPhaserEvents.SetCursorVisible({ visible: cursorVisible });
  }, [cursorVisible, updateKey]);

  // ---- Event Stream Update Loop ----
  // The osmdEventStream needs update() called periodically to detect missed
  // notes and color them red. This mirrors how MainAppPhaser calls
  // midiStream.update() via scheduler2.setIntervalByTick().
  const startEventStreamUpdateLoop = useCallback(() => {
    if (eventStreamUpdateLoopStartedRef.current || !scheduler2) {
      return;
    }
    eventStreamUpdateLoopStartedRef.current = true;
    scheduler2.setIntervalByTick(
      'updateOsmdEventStream',
      () => {
        osmdEventStream.update();
      },
      NoteType._32nd,
      1,
    );
  }, [scheduler2, osmdEventStream]);

  // ---- Accuracy Update Loop ----
  // The context's startAccuracyLoop reads from midiStream, which has no
  // events in OSMD mode (events are added to osmdEventStream in the scene).
  // This loop uses osmdEventStream instead and calls props.setAccuracy()
  // to drive the UI accuracy display. The context's loop will get NaN from
  // midiStream.calcRepAccuracy() and skip the setAccuracy call (isNaN guard),
  // so these two loops do not conflict.
  const startAccuracyUpdateLoop = useCallback(() => {
    if (accuracyLoopStartedRef.current || !scheduler2) {
      return;
    }
    accuracyLoopStartedRef.current = true;
    logger.debug('[OSMD-MIDI] Container: starting accuracy update loop using osmdEventStream');
    scheduler2.setIntervalByTick(
      'osmdAccuracyLoop',
      () => {
        const [accuracy] = osmdEventStream.calcRepAccuracy(
          currentTempo,
          performanceTempo ?? currentTempo,
        );
        logger.debug('[ACC-DBG] accuracyLoop tick', {
          accuracy,
          isNaN: isNaN(accuracy),
          accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
          totalNotes: osmdEventStream.totalNotesLength(),
        });
        if (!isNaN(accuracy)) {
          props.setAccuracy(accuracy);
        }
      },
      NoteType.QUARTER,
      1,
    );
  }, [scheduler2, osmdEventStream, currentTempo, performanceTempo, props.setAccuracy]);

  // ---- Container sizing via ResizeObserver ----
  // Always-current playing flag + last observed size, read from the (stable,
  // []-deps) ResizeObserver callback below.
  const repertoirePlayingRef = useRef(repertoirePlaying);
  repertoirePlayingRef.current = repertoirePlaying;
  const lastObservedSizeRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = debounce(() => {
      if (document.hidden) return;
      const { clientWidth, clientHeight } = container;
      if (clientWidth > 100 && clientHeight > 100) {
        // Detect a genuine viewport change (device rotation), ignoring the
        // initial observe and sub-pixel jitter.
        const prev = lastObservedSizeRef.current;
        const sizeChanged =
          prev !== null &&
          (Math.abs(clientWidth - prev.width) > 1 || Math.abs(clientHeight - prev.height) > 1);
        lastObservedSizeRef.current = { width: clientWidth, height: clientHeight };

        // Only update the wrapper height (for CSS) — NOT the game dimensions.
        // Updating gameWidth/gameHeight would cause PhaserGame to destroy and
        // recreate the entire Phaser game, wiping all phrases and cursors.
        // The scene's resize() method handles canvas + layout resizing via
        // this.scale.resize() and layoutPhrases(). Send the true client width
        // (no 960 floor) so the canvas fits in portrait, where the viewport is
        // narrower than the old landscape-only minimum.
        setWrapperHeight(clientHeight);
        if (sceneReadyRef.current) {
          NotifyPhaserEvents.ResizeWindow({
            width: clientWidth,
            height: clientHeight,
          });
        }

        // Pause on rotation. Recomputing the phrase layout seamlessly under
        // live playback is error-prone, so we deliberately pause and let the
        // user resume — the scene preserves the active phrase and resume
        // position across the layout rebuild.
        if (sizeChanged && repertoirePlayingRef.current) {
          dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
        }
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      handleResize.cancel();
    };
  }, [dispatch]);

  // ---- Start Phaser scene on PhaserReady ----
  const loadScene = useCallback(() => {
    if (!phaserRef.current?.scene) return;
    logger.debug('[OSMD-MIDI] Container: starting OSMDRepertoireScene', {
      osmdEventStreamId: osmdEventStream.id,
      gameWidth,
      gameHeight,
    });
    // Read from ref so loadScene stays stable (no repertoireSelected in deps).
    // Adding repertoireSelected to deps would cause useCallback to produce a
    // new function reference on every Redux update, which re-registers the
    // PhaserReady listener and risks missing the one-time event.
    const selectedRepertoire = repertoireSelectedRef.current;
    const repertoireTitle = selectedRepertoire?.name ?? '';
    const composerNames = selectedRepertoire?.composers
      ? getNamesConcatted(selectedRepertoire.composers)
      : '';
    logger.debug('[OSMD-MIDI] loadScene title/composer data', {
      hasSelectedRepertoire: !!selectedRepertoire,
      repertoireTitle,
      composerNames,
    });
    // Use the current Redux value (cursorVisibleRef is updated every render).
    // Pass to scene so init() has it before collectWaiting AddPhrase triggers createCursorForPhrase.
    const prefToSend = cursorVisibleRef.current;
    const sceneConfig = {
      eventStream: osmdEventStream,
      width: gameWidth,
      height: gameHeight,
      performanceTempo: performanceTempo ?? 72,
      title: repertoireTitle,
      composerName: composerNames,
      cursorVisible: prefToSend,
      // The horizontal "mobile" layout is for actual phones only. iPads (and
      // anything larger) always use the desktop vertical-stack layout, so key
      // this off device type, not viewport width — an iPad in portrait is
      // narrower than 960px but must NOT use the mobile layout.
      mobile: getDeviceClass() === 'phone',
      zoomLevel: zoomLevelRef.current,
      scoreTheme,
    };
    const scene = phaserRef.current.scene;
    const game = phaserRef.current.game;
    const sceneIsActive = scene?.sys?.isActive() || scene?.sys?.isPaused();

    // When restarting a running scene, scene.start() is async (processed by
    // Phaser's game loop). Any AddAllOSMDPhrases events sent before the
    // restart processes would be consumed by the OLD scene's listeners and
    // then destroyed when the old scene shuts down. Remove phrase listeners
    // now so phrases go into the waiting queue, where the new scene's init()
    // replays them via collectWaiting.
    if (sceneIsActive) {
      RemoveListener('AddPhraseOSMD');
      RemoveListener('AddAllOSMDPhrases');
    }

    if (sceneIsActive && scene) {
      scene.scene.start('OSMDRepertoireScene', sceneConfig);
    } else if (game) {
      game.scene.start('OSMDRepertoireScene', sceneConfig);
    } else {
      return;
    }
    sceneReadyRef.current = true;
    logger.info('[CURSOR-VIS] loadScene: sending SetCursorVisible', {
      cursorVisibleRef: prefToSend,
      userCursorPrefRef: userCursorPrefRef.current,
    });
    NotifyPhaserEvents.SetCursorVisible({ visible: prefToSend });
  }, [osmdEventStream, gameWidth, gameHeight, scoreTheme]);

  useEffect(() => {
    if (phaserRef.current?.wasReused) {
      // Game already booted from a previous mount — PhaserReady won't fire
      // again. Mark scene as ready so the phrase-send effect can call loadScene.
      sceneReadyRef.current = true;
      hasEverSentPhrasesRef.current = true;
    }
    PhaserToReactListeners.PhaserReady(loadScene, undefined, false, true);
    return () => {
      RemoveListener('PhaserReady', loadScene);
    };
  }, [loadScene]);

  // ---- Send phrases to scene when loaded ----
  useEffect(() => {
    if (allPhrasesLoaded && transformedPhrasePayloads.length > 0 && !phrasesSentRef.current) {
      const isSubsequentLoad = hasEverSentPhrasesRef.current;
      phrasesSentRef.current = true;
      hasEverSentPhrasesRef.current = true;

      // On subsequent piece loads the game is already booted (PhaserReady
      // won't fire again). Restart the scene so it re-runs init() and
      // registers listeners with collectWaiting before we dispatch phrases.
      // Skip on first load — PhaserReady already called loadScene.
      if (isSubsequentLoad && sceneReadyRef.current && phaserRef.current?.scene) {
        loadScene();
      }

      logger.info('[ACC-DBG] sending phrases to scene', {
        phraseCount: transformedPhrasePayloads.length,
        transposeSemitones: props.transposeSemitones,
        modeConversion: props.modeConversion,
        osmdEventStreamId: osmdEventStream.id,
        osmdTotalNotesBefore: osmdEventStream.totalNotesLength(),
        osmdAccuracyQueueLengthBefore: osmdEventStream.accuracyQueueLength(),
        contextMidiStreamId: midiStream.id,
        contextMidiTotalNotes: midiStream.totalNotesLength(),
      });
      NotifyPhaser(
        AddAllOSMDPhrasesHelper(
          transformedPhrasePayloads,
          props.transposeSemitones,
          true,
          true,
          scoreLoadIdRef.current,
        ),
      );
      // Log after sending — events are added asynchronously by the scene,
      // so totalNotesLength may not reflect the new events immediately.
      logger.info('[ACC-DBG] phrases sent — immediate post-send check', {
        osmdTotalNotesAfter: osmdEventStream.totalNotesLength(),
        osmdAccuracyQueueLengthAfter: osmdEventStream.accuracyQueueLength(),
      });
    }
  }, [
    allPhrasesLoaded,
    transformedPhrasePayloads,
    props.transposeSemitones,
    props.modeConversion,
    loadScene,
  ]);

  // A restored WebGL context can retain stems/lines while dropping
  // canvas-backed glyph textures. Rebuild the scene from the already
  // downloaded phrase payloads so the score returns atomically instead of
  // leaving a partially rendered page on screen.
  useEffect(() => {
    const recoverScoreRender = ({
      reason,
    }: {
      reason: 'webgl-context-restored' | 'game-restarted';
    }) => {
      if (
        renderRecoveryInProgressRef.current ||
        !allPhrasesLoaded ||
        transformedPhrasePayloads.length === 0
      ) {
        return;
      }

      renderRecoveryInProgressRef.current = true;
      phrasesSentRef.current = true;
      hasEverSentPhrasesRef.current = true;
      scoreLoadIdRef.current = uuid();
      setMusicLoaded(false);
      dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
      osmdEventStream.reset();

      logger.warn('[OSMDRepertoire] Rebuilding score after renderer recovery', {
        reason,
        phraseCount: transformedPhrasePayloads.length,
        loadId: scoreLoadIdRef.current,
      });

      loadScene();
      NotifyPhaser(
        AddAllOSMDPhrasesHelper(
          transformedPhrasePayloads,
          props.transposeSemitones,
          true,
          true,
          scoreLoadIdRef.current,
        ),
      );
    };

    PhaserToReactListeners.RepertoireRenderRecoveryRequested(
      recoverScoreRender,
      undefined,
      false,
      true,
    );
    return () => {
      RemoveListener('RepertoireRenderRecoveryRequested', recoverScoreRender);
    };
  }, [
    allPhrasesLoaded,
    transformedPhrasePayloads,
    props.transposeSemitones,
    dispatch,
    loadScene,
    osmdEventStream,
    setMusicLoaded,
  ]);

  // ---- Forward tempo changes to Phaser ----
  // When the user adjusts the tempo slider, currentTempo (derived from Redux)
  // updates. Forward the change to the scene so the count-in metronome click
  // rate and cursor blink speed update in real time.
  /** Always-current tempo value, readable from callbacks whose closures
   *  don't include currentTempo in their dependency arrays (e.g. the
   *  ListenStartedPlaying handler). */
  const currentTempoRef = useRef(currentTempo);
  currentTempoRef.current = currentTempo;

  const prevTempoRef = useRef(currentTempo);
  useEffect(() => {
    if (currentTempo !== prevTempoRef.current) {
      prevTempoRef.current = currentTempo;
      NotifyPhaserEvents.ChangeTempo({ tempo: currentTempo });
    }
  }, [currentTempo]);

  // MIDI forwarding to Phaser is now handled by the context's own listener
  // via the forwardMidiToPhaser flag (set above). This ensures MIDI events
  // survive clearListeners() calls in RepertoirePlayContext.init().

  // ---- Play / Pause forwarding to Phaser + event dispatching ----
  useEffect(() => {
    console.log(
      `[LOOP-DBG-243] Container play/pause useEffect fired: repertoirePlaying=${repertoirePlaying} musicLoaded=${musicLoaded} allPhrasesLoaded=${allPhrasesLoaded} hasScheduler2=${!!scheduler2} isFirstPlayPause=${isFirstPlayPauseRef.current}`,
    );
    // Skip the very first render to avoid spurious events
    if (isFirstPlayPauseRef.current) {
      isFirstPlayPauseRef.current = false;
      if (!repertoirePlaying) {
        return;
      }
    }

    if (repertoirePlaying) {
      if (!musicLoaded || !allPhrasesLoaded || !scheduler2) {
        logger.info('[OSMD-MIDI] play requested before OSMD playback was ready; deferring', {
          musicLoaded,
          allPhrasesLoaded,
          hasScheduler: !!scheduler2,
        });
        return;
      }

      // MUS-243: the playback machine owns Scheduler2 + TimeKeeper unpause.
      // The container's job here is to (a) signal user intent to the scene,
      // and (b) drive backend telemetry. The scene's StartExercise listener
      // dispatches USER_PLAY into the machine, whose facade actions handle
      // TK recalibration on resume (via `unpauseMainTKAt` reading the scene's
      // captured `lastPauseTimestamp`).
      console.log(`[LOOP-DBG] Container: sending StartExercise tempo=${currentTempo}`);
      NotifyPhaserEvents.StartExercise({
        tempo: currentTempo,
        autoStart: listenOnlyPlayback,
      });

      // Dispatch play event to backend (use refs for latest Redux values)
      if (
        !listenOnlyPlayback &&
        repertoireIdSelectRef.current !== undefined &&
        playSessionIdRef.current
      ) {
        dispatch(
          eventActions.repertoirePlayEventAction(
            repertoireIdSelectRef.current,
            playSessionIdRef.current,
          ),
        );
      }
    } else {
      console.log(
        '[LOOP-DBG-243] Container play/pause useEffect → emitting PauseExercise (repertoirePlaying=false)',
      );
      NotifyPhaserEvents.PauseExercise({});

      // Dispatch pause event to backend (use refs for latest Redux values)
      if (
        !listenOnlyPlayback &&
        repertoireIdSelectRef.current !== undefined &&
        playSessionIdRef.current
      ) {
        dispatch(
          eventActions.repertoirePauseEventAction(
            repertoireIdSelectRef.current,
            playSessionIdRef.current,
          ),
        );
      }
    }
  }, [allPhrasesLoaded, musicLoaded, repertoirePlaying, scheduler2]);

  // MUS-356: Reset the accumulated play-time counter only when a brand-new
  // play session begins. Replays reuse the same session id (MUS-243), so this
  // deliberately does not fire on soft refresh — the counter keeps growing
  // across play-throughs within a session, and the backend's MAX(existing,
  // incoming) write then reflects the true cumulative time.
  useEffect(() => {
    if (!playSessionId) {
      return;
    }
    accumulatedTimeRef.current = 0;
    lastMeasuredTimeRef.current = Date.now();
  }, [playSessionId]);

  // MUS-356: Play time accrues only while the machine is actually playing.
  // `repertoirePlaying` stays true for the whole active session — including the
  // between-iteration `loopCountInWait` waits and the pre-note count-in — so
  // measuring off it counted paused/waiting time (over-count during looping,
  // and it kept counting after the user stopped). Drive the measurement off the
  // real playback phase instead:
  //   • entering 'playing' → anchor the clock (preceding count-in / loop wait
  //     is not counted),
  //   • while 'playing'    → each tick, add elapsed and report the running
  //     total via RepertoirePlayingEvent,
  //   • leaving 'playing'  → bank the final partial segment and stop reporting
  //     (covers user pause, stop / piece end, and every loop wait).
  // accumulatedTimeRef stays monotonic across loops and play-throughs within a
  // session (reset only on a new session, above); the backend's MAX(existing,
  // incoming) write then reflects the true cumulative *playing* time.
  const isActivelyPlaying = playbackPhase === 'playing';
  useEffect(() => {
    if (listenOnlyPlayback || !isActivelyPlaying) {
      return;
    }

    lastMeasuredTimeRef.current = Date.now();

    sendPlayTimeIntervalRef.current = new IntervalTimer(() => {
      // Read from refs to get the latest Redux values — the effect closure
      // would otherwise capture stale values.
      const latestRepertoireId = repertoireIdSelectRef.current;
      const latestPlaySessionId = playSessionIdRef.current;
      const latestTempo = currentTempoRef.current;

      if (latestRepertoireId !== undefined && latestPlaySessionId) {
        const now = Date.now();
        accumulatedTimeRef.current += now - lastMeasuredTimeRef.current;
        lastMeasuredTimeRef.current = now;

        const [rawAccuracy, accuracyAtTempo] = osmdEventStream.calcRepAccuracy(
          latestTempo,
          performanceTempo ?? latestTempo,
        );
        // calcRepAccuracy returns NaN when the accuracy queue is empty
        // (no notes matched yet). NaN serializes to null in JSON, which
        // the backend rejects. Default to 0 until notes are marked.
        const playingAccuracy = isNaN(rawAccuracy) ? 0 : rawAccuracy;
        const totalNotesCount = getRepertoireProgressNoteCount(
          osmdEventStream.totalNotesLength(),
          totalScoreNoteCountRef.current,
        );
        let completionSum = 0;
        accuracyAtTempo.forEach((value: number) => (completionSum += value));
        const percentComplete = totalNotesCount > 0 ? (completionSum / totalNotesCount) * 100 : 0;

        logger.info('[ACC-TRACE] CONTAINER RepertoirePlayingEvent dispatch', {
          source: 'container-interval',
          rawAccuracy,
          playingAccuracy,
          isNaN_rawAccuracy: isNaN(rawAccuracy),
          typeof_rawAccuracy: typeof rawAccuracy,
          accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
          totalNotesCount,
          percentComplete,
          accumulatedTime: accumulatedTimeRef.current,
          latestTempo,
          performanceTempo,
          latestRepertoireId,
          latestPlaySessionId,
          osmdEventStreamId: osmdEventStream.id,
          valueSentToBackend: playingAccuracy,
        });

        dispatch(
          eventActions.repertoirePlayingEventAction(
            latestRepertoireId,
            latestPlaySessionId,
            accumulatedTimeRef.current,
            percentComplete,
            playingAccuracy,
          ) as ReturnType<typeof eventActions.repertoirePlayingEventAction>,
        );
      } else {
        logger.warn('[ACC-DBG] CONTAINER IntervalTimer skipped — missing IDs', {
          latestRepertoireId,
          latestPlaySessionId,
        });
      }
    }, PLAY_TIME_SEND_INTERVAL_MS);
    sendPlayTimeIntervalRef.current.start();

    return () => {
      // Leaving 'playing' (user pause, stop / piece end, or a loop wait): bank
      // the final partial segment so no playing time is lost, then stop
      // reporting until playing resumes.
      const now = Date.now();
      accumulatedTimeRef.current += now - lastMeasuredTimeRef.current;
      if (sendPlayTimeIntervalRef.current) {
        sendPlayTimeIntervalRef.current.pause();
        sendPlayTimeIntervalRef.current.clear();
        sendPlayTimeIntervalRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivelyPlaying, listenOnlyPlayback]);

  // ---- Listen for StartedPlaying from Phaser ----
  // When the user plays the first note during count-in, the Phaser scene
  // emits StartedPlaying with a processEventsQueue callback. We must
  // invoke that callback to flush the queued first-note MIDI event
  // through the event stream so it gets matched and colored.
  // We also call startScheduler() here to perform the deferred
  // scheduler2.unpauseTicks(), mirroring how MainAppPhaser calls onStart
  // only when it receives StartedPlaying from the scene.
  useEffect(() => {
    console.log(`[LOOP-DBG] Container: registering StartedPlaying listener`);
    const handleStartedPlaying = async (args: StartPlayingArgs | void) => {
      console.log(
        `[LOOP-DBG] Container: StartedPlaying received hasProcessEventsQueue=${!!args?.processEventsQueue} measureOffset=${args?.measureOffset} hasPlayedOnce=${hasPlayedOnceRef.current}`,
      );
      logger.info('[ACC-DBG] StartedPlaying received — event stream state', {
        hasProcessEventsQueue: !!args?.processEventsQueue,
        measureOffset: args?.measureOffset,
        hasPlayedOnce: hasPlayedOnceRef.current,
        osmdEventStreamId: osmdEventStream.id,
        osmdTotalNotes: osmdEventStream.totalNotesLength(),
        osmdAccuracyQueueLength: osmdEventStream.accuracyQueueLength(),
        contextMidiStreamId: midiStream.id,
        contextMidiTotalNotes: midiStream.totalNotesLength(),
        contextMidiAccuracyQueueLength: midiStream.accuracyQueueLength(),
        timeKeeperPausedBefore: midiStream.timeKeeper.isPaused(),
      });

      // MUS-243: Main TimeKeeper + Scheduler2 lifecycle is owned by the
      // playback machine. Container's StartedPlaying handler only tracks
      // first-play vs subsequent (so update loops start once) and forwards
      // any queued events (the legacy `processEventsQueue` callback — kept
      // for safety; current emissions pass `undefined`).
      if (!hasPlayedOnceRef.current) {
        console.log(`[LOOP-DBG] Container: StartedPlaying — first play (machine drives TK)`);
        hasPlayedOnceRef.current = true;
      } else {
        console.log(`[LOOP-DBG] Container: StartedPlaying — subsequent (machine drives TK)`);
      }

      console.log(
        `[LOOP-DBG] Container: StartedPlaying — after if/else block hasPlayedOnce=${hasPlayedOnceRef.current} timeKeeperPaused=${midiStream.timeKeeper.isPaused()}`,
      );
      logger.debug('[OSMD-MIDI] Container: after startScheduler()', {
        timeKeeperPausedAfter: midiStream.timeKeeper.isPaused(),
      });
      if (!listenOnlyPlayback) {
        // Start the event stream update loop now that the scheduler is running.
        // This calls osmdEventStream.update() periodically to detect missed
        // notes and color them red (NEVER_PLAYED state).
        startEventStreamUpdateLoop();
        // Start the accuracy update loop so the UI accuracy display reflects
        // notes matched in osmdEventStream (not the context's empty midiStream).
        startAccuracyUpdateLoop();
      }
      if (args?.processEventsQueue) {
        const [processQueueFn, processQueueScope] = args.processEventsQueue;
        logger.debug('[OSMD-MIDI] Container: calling processEventsQueue NOW');
        processQueueFn.call(processQueueScope);
        logger.debug('[OSMD-MIDI] Container: processEventsQueue returned');
      }
    };
    PhaserToReactListeners.StartedPlaying(handleStartedPlaying);
    return () => {
      console.log(`[LOOP-DBG] Container: removing StartedPlaying listener`);
      RemoveListener('StartedPlaying', handleStartedPlaying);
    };
  }, [
    midiStream,
    osmdEventStream,
    startEventStreamUpdateLoop,
    startAccuracyUpdateLoop,
    listenOnlyPlayback,
  ]);

  // ---- Listen for ExercisePaused from Phaser ----
  // When the scene pauses and repositions the cursor to the last completed
  // measure, it emits ExercisePaused(lastPlayedMeasure). We store this so
  // that on resume, startScheduler() can pass it as measureOffset, ensuring
  // the TimeKeeper starts from exactly where the cursor was placed.
  useEffect(() => {
    console.log(`[LOOP-DBG] Container: registering ExercisePaused listener`);
    const handleExercisePaused = ({
      lastPlayedMeasure,
      pausedAtTimestamp,
    }: {
      lastPlayedMeasure?: number;
      pausedAtTimestamp?: number;
    }) => {
      console.log(
        `[LOOP-DBG] Container: ExercisePaused received lastPlayedMeasure=${lastPlayedMeasure} pausedAtTimestamp=${pausedAtTimestamp}`,
      );
      logger.debug('[OSMD-MIDI] Container: ExercisePaused received', {
        lastPlayedMeasure,
        pausedAtTimestamp,
      });
      pausedAtMeasureRef.current = lastPlayedMeasure;
      pausedAtTimestampRef.current = pausedAtTimestamp;
    };
    PhaserToReactListeners.ExercisePaused(handleExercisePaused);
    return () => {
      console.log(`[LOOP-DBG] Container: removing ExercisePaused listener`);
      RemoveListener('ExercisePaused', handleExercisePaused);
    };
  }, []);

  // ---- Listen for Phaser completion events ----
  useEffect(() => {
    console.log(
      `[LOOP-DBG] Container: registering RepertoireCompleted + RepertoirePhraseCompleted listeners`,
    );
    const handleRepertoireCompleted = () => {
      if (listenOnlyPlayback) {
        logger.info('[ACC-TRACE] CONTAINER RepertoireCompleted lifecycle-only stop (listen mode)');
        dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
        dispatch(repertoireActions.setRepertoireEnded(true));
        return;
      }

      // Read from refs to get the latest Redux values — this handler is
      // registered once and its closure would otherwise capture stale values.
      const latestRepertoireId = repertoireIdSelectRef.current;
      const latestPlaySessionId = playSessionIdRef.current;
      const latestTempo = currentTempoRef.current;

      logger.info('RepertoirePlayContainerOSMD: RepertoireCompleted received from Phaser');
      if (latestRepertoireId !== undefined && latestPlaySessionId) {
        const [rawCompletedAccuracy, completedAccuracyAtTempo] = osmdEventStream.calcRepAccuracy(
          latestTempo,
          performanceTempo ?? latestTempo,
        );
        // calcRepAccuracy returns NaN when the accuracy queue is empty
        // (no notes matched yet). NaN serializes to null in JSON, which
        // the backend rejects. Default to 0 until notes are marked.
        const completedAccuracy = isNaN(rawCompletedAccuracy) ? 0 : rawCompletedAccuracy;
        const totalNotesCount = getRepertoireProgressNoteCount(
          osmdEventStream.totalNotesLength(),
          totalScoreNoteCountRef.current,
        );
        let completionSum = 0;
        completedAccuracyAtTempo.forEach((value: number) => (completionSum += value));
        const completion = totalNotesCount > 0 ? (completionSum / totalNotesCount) * 100 : 0;

        logger.info('[ACC-TRACE] CONTAINER RepertoireCompletedEvent dispatch', {
          source: 'container-completed',
          rawCompletedAccuracy,
          completedAccuracy,
          isNaN_rawAccuracy: isNaN(rawCompletedAccuracy),
          typeof_rawCompletedAccuracy: typeof rawCompletedAccuracy,
          accuracyQueueLength: osmdEventStream.accuracyQueueLength(),
          totalNotesCount,
          completion,
          latestTempo,
          performanceTempo,
          latestRepertoireId,
          latestPlaySessionId,
          osmdEventStreamId: osmdEventStream.id,
          valueSentToBackend: completedAccuracy,
        });

        dispatch(
          eventActions.repertoireCompletedEventAction(
            latestRepertoireId,
            latestPlaySessionId,
            completion,
            completedAccuracy,
          ) as ReturnType<typeof eventActions.repertoireCompletedEventAction>,
        );
      } else {
        logger.warn('[ACC-DBG] CONTAINER RepertoireCompleted skipped — missing IDs', {
          latestRepertoireId,
          latestPlaySessionId,
        });
      }
    };
    PhaserToReactListeners.RepertoireCompleted(handleRepertoireCompleted);

    const handleRepertoirePhraseCompleted = ({ phraseIndex }: { phraseIndex: number }) => {
      if (listenOnlyPlayback) {
        logger.info('[ACC-TRACE] CONTAINER RepertoirePhraseCompleted SKIPPED (listen mode)');
        return;
      }

      const phrase = phrases[phraseIndex];
      // Read from refs for latest Redux values
      const latestPlaySessionId = playSessionIdRef.current;
      const latestRepertoireId = repertoireIdSelectRef.current;
      if (phrase && latestPlaySessionId && latestRepertoireId !== undefined) {
        // Delay dispatch by 2 seconds to capture the final note offset
        setTimeout(() => {
          if (phrase.getComplete()) return;
          phrase.setComplete(true);

          const phraseData = phrase.generateRepPhraseCompletedEventData();
          dispatch(
            eventActions.repPhraseCompletedEventAction({
              midiData: [],
              errorData: phraseData.errorData,
              accuracy: phraseData.accuracy,
              lHAccuracy: phraseData.lhAccuracy,
              rHAccuracy: phraseData.rhAccuracy,
              totalNotes: phraseData.totalNotes,
              lhTotalNotes: phraseData.lhTotalNotes,
              rhTotalNotes: phraseData.rhTotalNotes,
              errorScore: phraseData.errorScore,
              lhErrorScore: phraseData.lhErrorScore,
              rhErrorScore: phraseData.rhErrorScore,
              phraseS3Id: phrase.uuid,
              repertoireId: latestRepertoireId,
              bpm: phraseData.bpm,
              playSessionId: latestPlaySessionId,
            }) as ReturnType<typeof eventActions.repPhraseCompletedEventAction>,
          );
        }, 2000);
      }
    };
    PhaserToReactListeners.RepertoirePhraseCompleted(handleRepertoirePhraseCompleted);

    return () => {
      console.log(
        `[LOOP-DBG] Container: removing RepertoireCompleted + RepertoirePhraseCompleted listeners`,
      );
      RemoveListener('RepertoireCompleted', handleRepertoireCompleted);
      RemoveListener('RepertoirePhraseCompleted', handleRepertoirePhraseCompleted);
    };
  }, [
    phrases,
    playSessionId,
    repertoireIdSelect,
    currentTempo,
    performanceTempo,
    midiStream,
    dispatch,
    osmdEventStream,
    listenOnlyPlayback,
  ]);

  // ---- Update user-repertoire-progress using osmdEventStream ----
  // In OSMD mode, the context's progress update (which uses midiStream with
  // VexFlow iterators) is skipped. This effect uses osmdEventStream (which
  // has the correct OSMD-based events) to send accurate percent_complete
  // and accuracy values when playback stops.
  const isFirstProgressUpdateRef = useRef(true);
  useEffect(() => {
    // Skip the initial render (musicLoaded may already be true on mount)
    if (isFirstProgressUpdateRef.current) {
      isFirstProgressUpdateRef.current = false;
      return;
    }
    if (!musicLoaded || repertoirePlaying) return;
    if (listenOnlyPlayback) {
      logger.info('[ACC-TRACE] CONTAINER progress update SKIPPED (listen mode)');
      return;
    }

    const latestRepertoireId = repertoireIdSelectRef.current;
    const latestTempo = currentTempoRef.current;

    const [rawAccuracy, accuracyAtTempo] = osmdEventStream.calcRepAccuracy(
      latestTempo,
      performanceTempo ?? latestTempo,
    );
    const accuracy = isNaN(rawAccuracy) ? 0 : rawAccuracy;
    const totalNotesCount = getRepertoireProgressNoteCount(
      osmdEventStream.totalNotesLength(),
      totalScoreNoteCountRef.current,
    );
    let completionSum = 0;
    accuracyAtTempo.forEach((value: number) => (completionSum += value));
    const percentComplete = totalNotesCount > 0 ? completionSum / totalNotesCount : 0;

    let repertoireStatus = REPERTOIRE_PROGRESS.IN_PROGRESS;
    if (percentComplete >= 1.0) {
      repertoireStatus = REPERTOIRE_PROGRESS.COMPLETE;
    }

    // Compare with previous best values — never regress the user's progress
    let bestAccuracy: number;
    let bestPercentComplete: number;
    if (
      repertoireProgressSelected &&
      repertoireProgressSelected.percent_complete &&
      100 * percentComplete < repertoireProgressSelected.percent_complete
    ) {
      bestAccuracy = repertoireProgressSelected.accuracy ?? 0;
      bestPercentComplete = repertoireProgressSelected.percent_complete;
    } else {
      bestAccuracy = accuracy;
      bestPercentComplete = 100 * percentComplete;
    }

    const progressData = {
      status: repertoireStatus,
      repertoire: { id: latestRepertoireId },
      last_played_at: new Date().toISOString(),
      completed_at:
        repertoireStatus === REPERTOIRE_PROGRESS.COMPLETE ? new Date().toISOString() : null,
      accuracy: bestAccuracy,
      percent_complete: bestPercentComplete,
      last_tempo: latestTempo,
    };
    logger.info('[ACC-TRACE] CONTAINER progress update dispatch', progressData);
    dispatch(repertoireActions.updateRepertoireDataRequest({ ...progressData }));
  }, [listenOnlyPlayback, musicLoaded, repertoirePlaying]);

  // ---- Container-scoped refs reset on soft refresh ----
  // RepertoirePlayContext.softRefreshRepertoire() increments updateKey and
  // emits SoftRefreshRepertoire directly to the scene (synchronously, inside
  // the softRefreshRepertoire call itself). This useEffect handles the
  // container-scoped state that the context can't touch from its own scope:
  // the once-per-session ref guards that gate update loops and first-play
  // behavior, and the auto-restart dispatch that sets repertoirePlaying=true
  // so the UI shows the pause button while the scene's count-in is active.
  useEffect(() => {
    if (updateKey !== prevUpdateKeyRef.current) {
      prevUpdateKeyRef.current = updateKey;
      logger.info('RepertoirePlayContainerOSMD: soft refresh detected, resetting container refs');

      // Reset phrase-sent flag so phrases can be re-sent if needed after refresh
      // (In practice, phrases are already loaded; the scene's softReset handles visuals)
      phrasesSentRef.current = true;

      // Reset the update loop flags so they can be restarted on next play
      eventStreamUpdateLoopStartedRef.current = false;
      accuracyLoopStartedRef.current = false;

      // Reset first-play tracking so the next play cycle uses the deferred
      // scheduler pattern (count-in → first MIDI note → startScheduler)
      hasPlayedOnceRef.current = false;

      // Clear the paused measure/timestamp — fresh play cycle after soft refresh
      pausedAtMeasureRef.current = undefined;
      pausedAtTimestampRef.current = undefined;

      // Clear repertoireEnded so the UI can transition out of the
      // ended-piece state. repertoirePlaying is no longer dispatched
      // explicitly here — it's mirrored from the machine phase by the
      // reducer's SET_PLAYBACK_PHASE case. scene.softReset dispatches
      // USER_PLAY into the machine, which lands in count-in state, which
      // mirrors repertoirePlaying=true via the phase update. The Redux
      // round-trip that previously raced with playLevel's post-await
      // setRepertoirePlaying(true) is gone.
      dispatch(repertoireActions.setRepertoireEnded(false));
    }
  }, [updateKey, dispatch]);

  // ---- Pause on visibility hidden (tab switch / window minimize) ----
  // MUS-243: emit AppBackgrounded / AppForegrounded into the playback
  // machine. The machine's APP_BACKGROUNDED transition from `playing` or
  // `normalCountIn` lands in `paused`; APP_FOREGROUNDED is a no-op in
  // `paused` (user must press play to resume). To preserve the legacy
  // auto-resume behavior, we ALSO dispatch setRepertoirePlaying(true) on
  // return — this triggers the play/pause useEffect to re-emit
  // StartExercise, which routes back through the machine as USER_PLAY.
  const pausedByBackgroundRef = useRef(false);
  useEffect(() => {
    const handleVisibilityChange = () => {
      console.log(
        `[LOOP-DBG-243] Container visibilitychange fired: document.hidden=${document.hidden} repertoirePlaying=${repertoirePlaying}`,
      );
      if (document.hidden) {
        if (repertoirePlaying) {
          console.log('[LOOP-DBG-243] Container visibilitychange → emitting AppBackgrounded');
          pausedByBackgroundRef.current = true;
          NotifyPhaserEvents.AppBackgrounded();
          dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
        }
      } else {
        if (pausedByBackgroundRef.current) {
          pausedByBackgroundRef.current = false;
          NotifyPhaserEvents.AppForegrounded();
          dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: true }));
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [repertoirePlaying, dispatch]);

  // ---- Cleanup on unmount ----
  useEffect(() => {
    console.log(`[LOOP-DBG] Container: registering cleanup-on-unmount effect`);
    return () => {
      console.log(`[LOOP-DBG] Container: cleanup-on-unmount running`);
      if (sendPlayTimeIntervalRef.current) {
        sendPlayTimeIntervalRef.current.pause();
        sendPlayTimeIntervalRef.current.clear();
        sendPlayTimeIntervalRef.current = undefined;
      }
    };
  }, []);

  // ---- Render ----
  const scoreIsLoading = !(musicLoaded && allPhrasesLoaded);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative', // positioning context for the feedback toast overlay
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100vw',
        flex: 1, // fill parent height (main container has flex: 1 in RepertoirePlay)
        minHeight: 0, // allow flex shrink below content size
        backgroundColor:
          scoreTheme !== 'light' && scoreIsLoading ? scorePalette.repertoireBackground : undefined,
      }}
    >
      {scoreIsLoading && <CustomCircularProgress />}
      {feedbackToastText && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(48px + env(safe-area-inset-top, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            pointerEvents: 'none',
          }}
        >
          <FeedbackToast text={feedbackToastText} />
        </div>
      )}
      {perfectedToastVisible && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            pointerEvents: 'none',
          }}
        >
          <FeedbackToast text="You have perfected this section" />
        </div>
      )}
      <div
        className="osmd-repertoire-game-wrapper"
        style={{
          width: '100%',
          height: `${wrapperHeight}px`,
          backgroundColor: scorePalette.repertoireBackground,
          // Hide the game wrapper while loading — keep it in the DOM so the
          // Phaser game instance (and its WebGL context) stays alive.
          // Use position:absolute so it doesn't push layout while hidden,
          // but keep full dimensions so Phaser can initialize its canvas.
          ...(!scoreIsLoading ? {} : { position: 'absolute' as const, left: '-9999px' }),
        }}
      >
        {/*
          PhaserGame sets pointer-events: none on #game-container for the
          lesson section (where the game area shouldn't capture clicks).
          Repertoire needs pointer events for scroll input, so we re-enable
          them via an inline <style> scoped to this wrapper.
        */}
        <style>{`.osmd-repertoire-game-wrapper #game-container { pointer-events: auto !important; }`}</style>
        <div
          style={{
            width: '100%',
            height: headerVisible ? '35px' : '0px',
            transition: 'height 0.5s ease-in-out',
          }}
        ></div>
        <PhaserGame
          width={gameWidth}
          height={gameHeight}
          ref={phaserRef}
          scaling={Phaser.Scale.NONE}
          crisp={true}
        />
      </div>
    </div>
  );
};

export default RepertoirePlayContainerOSMD;
